import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useScopeControlCounts } from '../../../hooks/analytics/useScopeReports.ts';
import { WidgetCard } from '../controller/shared.tsx';
import { CHART_TOOLTIP_STYLE, SEMANTIC } from '../theme.ts';

/** `scope_key_nonkey` — donut of key vs. non-key controls in scope. */
export const ScopeKeyNonKeyDonut: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useScopeControlCounts();

  const pieData = useMemo(
    () => [
      { name: 'Key', value: data?.key_count ?? 0, color: SEMANTIC.info.chart },
      { name: 'Non-Key', value: data?.non_key_count ?? 0, color: SEMANTIC.neutral.chart },
    ],
    [data]
  );
  const total = pieData.reduce((sum, d) => sum + d.value, 0);

  return (
    <WidgetCard
      title="Key vs. Non-Key Controls"
      description="Share of in-scope controls flagged key"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load key/non-key split.'}
      isEmpty={!isLoading && !isError && total === 0}
      emptyMessage="No controls in scope yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="relative h-40 w-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={3} stroke="none">
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-slate-900">{total}</span>
            <span className="text-[10px] text-slate-500">controls</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600">
          {pieData.map((entry) => (
            <span key={entry.name} className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name} ({entry.value})
            </span>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
};

export default ScopeKeyNonKeyDonut;
